import { useState } from "react";
import { ArrowRight, Loader2, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { ChipGroup } from "../ui/chip-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { cn } from "@/lib/utils";
import { pillColorClasses } from "../ui/pill";
import { STATUS_LABELS, PRIORITY_LABELS, statusPillKind, priorityPillKind, StatusBadge, PriorityBadge } from "../VacatureBadges";
import { updateVacature, deleteVacature, Vacature } from "../../api/api";
import { toast } from "../../lib/toast";

// Runs one request per vacature (the backend has no bulk endpoint, so every
// change still goes through the normal per-vacature RLS checks and activity log)
// and reports the ids that failed, so the caller can keep those selected.
async function runForEach(ids: number[], fn: (id: number) => Promise<unknown>): Promise<number[]> {
  const results = await Promise.allSettled(ids.map(fn));
  return ids.filter((_, i) => results[i].status === "rejected");
}

function vacatureCount(n: number) {
  return `${n} ${n === 1 ? "vacature" : "vacatures"}`;
}

/** Blue bar shown above the table while bulk-select mode is on. */
export function BulkSelectBar({
  selectedCount,
  visibleCount,
  allSelected,
  onToggleAll,
  allPagesCount,
  allPagesSelected,
  loadingAll,
  onSelectAllPages,
  onCancel,
  onEdit,
  onDelete,
}: {
  selectedCount: number;
  visibleCount: number;
  allSelected: boolean;
  onToggleAll: () => void;
  /** Vacatures matching the current filters across all pages, or null when they all fit on this page. */
  allPagesCount: number | null;
  allPagesSelected: boolean;
  loadingAll: boolean;
  onSelectAllPages: () => void;
  onCancel: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div data-tour="bulk-bar" data-tour-count={selectedCount} className="flex flex-wrap items-center gap-3 rounded-card border border-waves-zee bg-waves-mist py-3 pl-5 pr-4">
      <span className="text-[15px] font-bold text-waves-ink">
        {selectedCount === 0 ? "Selecteer vacatures" : `${vacatureCount(selectedCount)} geselecteerd`}
      </span>
      <button
        type="button"
        onClick={onToggleAll}
        disabled={visibleCount === 0}
        className="text-sm font-bold text-waves-accent hover:underline disabled:opacity-50"
      >
        {allSelected || allPagesSelected ? "Selectie wissen" : `Alles op deze pagina selecteren (${visibleCount})`}
      </button>
      {allPagesCount !== null && !allPagesSelected && (
        <button
          type="button"
          onClick={onSelectAllPages}
          disabled={loadingAll}
          className="flex items-center gap-1.5 text-sm font-bold text-waves-accent hover:underline disabled:opacity-50"
        >
          {loadingAll && <Loader2 size={14} className="animate-spin" />}
          Alle {vacatureCount(allPagesCount)} op alle pagina's selecteren
        </button>
      )}
      <div className="ml-auto flex gap-2">
        <Button variant="outline" onClick={onCancel}>Annuleren</Button>
        <Button variant="destructive" disabled={selectedCount === 0} onClick={onDelete}>
          <Trash2 /> Verwijderen
        </Button>
        <Button data-tour="bulk-edit" disabled={selectedCount === 0} onClick={onEdit}>Aanpassen</Button>
      </div>
    </div>
  );
}

type Field = "status" | "priority";
type Form = Record<Field, string | null>;

const KEEP = "__keep";
const FIELD_LABELS: Record<Field, string> = { status: "Status", priority: "Prioriteit" };
const FIELDS: Field[] = ["status", "priority"];
const FIELD_BADGES: Record<Field, typeof StatusBadge> = { status: StatusBadge, priority: PriorityBadge };

function chipOptions(labels: Record<string, string>, pillKind: (v: string) => Parameters<typeof pillColorClasses>[0]) {
  return [
    { value: KEEP, label: "Niet wijzigen" },
    ...Object.entries(labels).map(([value, label]) => ({
      value,
      label,
      activeClassName: cn("border-transparent", pillColorClasses(pillKind(value))),
    })),
  ];
}
const STATUS_CHIPS = chipOptions(STATUS_LABELS, statusPillKind);
const PRIORITY_CHIPS = chipOptions(PRIORITY_LABELS, priorityPillKind);

/** The fields of `v` that `form` would actually change (fields already at the target value are skipped). */
function changesFor(v: Vacature, form: Form): Field[] {
  return FIELDS.filter(f => form[f] !== null && v[f] !== form[f]);
}

/**
 * Two-step bulk edit: pick the new status/priority ("Niet wijzigen" leaves a field
 * alone per vacature), then review exactly which vacatures change before applying.
 * The success toast offers an undo that restores each vacature's previous values.
 */
export default function BulkEditModal({
  selected,
  onClose,
  onDone,
  onUndone,
}: {
  selected: Vacature[];
  onClose: () => void;
  /** Called after applying, with the ids whose request failed. */
  onDone: (failedIds: number[]) => void;
  /** Called after an undo from the toast has finished. */
  onUndone: () => void;
}) {
  const [step, setStep] = useState<"edit" | "confirm">("edit");
  const [form, setForm] = useState<Form>({ status: null, priority: null });
  const [busy, setBusy] = useState(false);

  const hasChange = form.status !== null || form.priority !== null;
  const preview = selected.map(v => ({ v, fields: changesFor(v, form) }));
  const changed = preview.filter(p => p.fields.length > 0);
  const skipped = preview.length - changed.length;

  async function apply() {
    if (changed.length === 0) { onClose(); return; }
    const byId = new Map(changed.map(p => [p.v.id, p]));
    const bodyFor = (id: number, source: Form | Vacature) => {
      const body: Partial<Record<Field, string>> = {};
      for (const f of byId.get(id)!.fields) body[f] = source[f] as string;
      return body;
    };

    setBusy(true);
    try {
      const ids = [...byId.keys()];
      // A bulk edit also counts as "Bevestig actueel": it resets each vacature's
      // stale counter (the backend stamps reviewed_at with its own clock).
      const reviewed_at = new Date().toISOString();
      const failed = await runForEach(ids, id => updateVacature(id, { ...bodyFor(id, form), reviewed_at }, { silenceErrors: true }));
      const succeeded = ids.filter(id => !failed.includes(id));
      if (failed.length === 0) {
        toast(`${vacatureCount(succeeded.length)} bijgewerkt`, {
          kind: "success",
          action: {
            label: "Ongedaan maken",
            onClick: async () => {
              const undoFailed = await runForEach(succeeded, id =>
                updateVacature(id, bodyFor(id, byId.get(id)!.v), { silenceErrors: true }));
              if (undoFailed.length > 0) toast(`Ongedaan maken mislukt voor ${vacatureCount(undoFailed.length)}`, { kind: "error" });
              onUndone();
            },
          },
        });
      } else {
        toast(`${succeeded.length} bijgewerkt, ${failed.length} mislukt`, { kind: "error" });
      }
      onDone(failed);
    } finally {
      setBusy(false);
    }
  }

  const summaryFields = FIELDS.filter(f => form[f] !== null);

  return (
    <Dialog open onOpenChange={open => { if (!open && !busy) onClose(); }}>
      <DialogContent className="max-h-[calc(100vh-64px)] max-w-[600px] overflow-y-auto">
        <DialogHeader>
          <span className="text-[11px] font-bold uppercase tracking-label text-waves-muted">
            {step === "edit" ? "Stap 1 van 2 · Aanpassen" : "Stap 2 van 2 · Controleren"}
          </span>
          <DialogTitle>
            {step === "edit"
              ? `${vacatureCount(selected.length)} aanpassen`
              : `${vacatureCount(changed.length)} ${changed.length === 1 ? "wordt" : "worden"} aangepast`}
          </DialogTitle>
          {step === "edit" && (
            <DialogDescription>
              Kies alleen wat je wilt veranderen. Velden op "Niet wijzigen" blijven per vacature zoals ze zijn.
            </DialogDescription>
          )}
        </DialogHeader>

        {step === "edit" ? (
          <>
            <div data-tour="bulk-form" className="flex flex-col gap-2">
              <span className="text-[13px] font-bold">Status</span>
              <ChipGroup
                aria-label="Status"
                options={STATUS_CHIPS}
                value={form.status ?? KEEP}
                onChange={v => setForm(f => ({ ...f, status: v === KEEP ? null : v }))}
              />
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-[13px] font-bold">Prioriteit</span>
              <ChipGroup
                aria-label="Prioriteit"
                options={PRIORITY_CHIPS}
                value={form.priority ?? KEEP}
                onChange={v => setForm(f => ({ ...f, priority: v === KEEP ? null : v }))}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Annuleren</Button>
              <Button data-tour="bulk-next" disabled={!hasChange} onClick={() => setStep("confirm")}>Controleren</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              {summaryFields.map(f => {
                const Badge = FIELD_BADGES[f];
                return (
                  <span key={f} className="flex items-center gap-2 rounded-pill bg-waves-mist py-1.5 pl-3.5 pr-1.5 text-sm font-bold text-waves-ink">
                    {FIELD_LABELS[f]} <ArrowRight size={13} className="text-waves-muted" /> <Badge value={form[f]!} />
                  </span>
                );
              })}
            </div>
            {changed.length > 0 && (
              <ul className="max-h-[320px] overflow-y-auto rounded-card border border-waves-line">
                {changed.map(({ v, fields }, i) => (
                  <li key={v.id} className={cn("flex items-center gap-3 px-4 py-2.5", i > 0 && "border-t border-waves-line")}>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-bold text-waves-ink">{v.functie}</span>
                      {v.locatie && <span className="truncate text-xs text-waves-muted">{v.locatie}</span>}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      {fields.map(f => {
                        const Badge = FIELD_BADGES[f];
                        return (
                          <span key={f} className="flex items-center gap-1.5 whitespace-nowrap">
                            <span className="text-[13px] text-waves-muted">{FIELD_LABELS[f]}</span>
                            <Badge value={v[f]} />
                            <ArrowRight size={13} className="shrink-0 text-waves-muted" />
                            <Badge value={form[f]!} />
                          </span>
                        );
                      })}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {changed.length > 0 && (
              <p className="text-[13px] text-waves-muted">
                Aangepaste vacatures worden ook als actueel bevestigd.
              </p>
            )}
            {skipped > 0 && (
              <p className="text-[13px] text-waves-muted">
                {skipped} {skipped === 1 ? "vacature heeft" : "vacatures hebben"} al deze waarde en blijft ongewijzigd.
              </p>
            )}
            <DialogFooter>
              <Button variant="outline" disabled={busy} onClick={() => setStep("edit")}>← Terug</Button>
              <Button data-tour="bulk-apply" disabled={busy} onClick={apply}>
                {busy && <Loader2 className="animate-spin" />}
                {changed.length === 0
                  ? "Sluiten"
                  : `Bevestig ${changed.length} ${changed.length === 1 ? "wijziging" : "wijzigingen"}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Confirmation for deleting every selected vacature; lists them so nothing is removed by surprise. */
export function BulkDeleteModal({
  selected,
  onClose,
  onDone,
}: {
  selected: Vacature[];
  onClose: () => void;
  /** Called after deleting, with the ids whose request failed. */
  onDone: (failedIds: number[]) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function confirm() {
    setBusy(true);
    try {
      const failed = await runForEach(selected.map(v => v.id), id => deleteVacature(id, { silenceErrors: true }));
      const ok = selected.length - failed.length;
      if (failed.length === 0) toast(`${vacatureCount(ok)} verwijderd`, { kind: "success" });
      else toast(`${ok} verwijderd, ${failed.length} mislukt`, { kind: "error" });
      onDone(failed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={open => { if (!open && !busy) onClose(); }}>
      <DialogContent className="max-h-[calc(100vh-64px)] max-w-[600px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{vacatureCount(selected.length)} verwijderen?</DialogTitle>
          <DialogDescription>Dit kan niet ongedaan worden gemaakt.</DialogDescription>
        </DialogHeader>
        <ul className="max-h-[320px] overflow-y-auto rounded-card border border-waves-line">
          {selected.map((v, i) => (
            <li key={v.id} className={cn("flex flex-col px-4 py-2.5", i > 0 && "border-t border-waves-line")}>
              <span className="truncate text-sm font-bold text-waves-ink">{v.functie}</span>
              {v.locatie && <span className="truncate text-xs text-waves-muted">{v.locatie}</span>}
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose}>Annuleren</Button>
          <Button variant="destructive" disabled={busy} onClick={confirm}>
            {busy ? <Loader2 className="animate-spin" /> : <Trash2 />}
            Verwijder {vacatureCount(selected.length)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
